import axiosApiClient from "../axios";
import {getBackendUrl} from "../../utils/config";

const PATH = '/api/child'

export const getAllChildren = async (branchId = null, roomId = null) => {
    const params = {};
    if (branchId != null) params.branchId = branchId;
    if (roomId != null) params.roomId = roomId;
    return await axiosApiClient.get(PATH, { params });
};

export const getChildrenById = async (childId) => {
    return await axiosApiClient.get(`${PATH}/${childId}`);
};
export const getChildrenDetailById = async (childId) => {
    return await axiosApiClient.get(`${PATH}/${childId}/detail`);
};

export const getChildDetail = async (childId) => {
    return await axiosApiClient.get(`${PATH}/${childId}/detail`);
};

export const getAllChildrenByGuardianId = async (guardianId) => {
    return await axiosApiClient.get(`${PATH}/guardian/${guardianId}`);
};

export const addChild = async (child) => {
    try {
        return await axiosApiClient.post(PATH, child, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    } catch (err) {
        throw new Error(err.response?.data?.message || 'Add Child failed');
    }
};

export const updateChild = async (id, child) => {
    try {
        return await axiosApiClient.put(`${PATH}/${id}`, child, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    } catch (err) {
        throw new Error(err.response?.data?.message || 'Add Child failed');
    }
};

export const deleteChild = async (childId) => {
    try {
        return await axiosApiClient.delete(`${PATH}/${childId}`);
    } catch (err) {
        throw new Error(err.response?.data?.message || 'Delete Child failed');
    }
};

export const removeGuardianFromChild = async (childId, guardianId) => {
    try {
        return await axiosApiClient.delete(`${PATH}/${childId}/guardians/${guardianId}`);
    } catch (err) {
        throw new Error(err.response?.data?.message || 'Remove Guardian from Child failed');
    }
};

export const addGuardianToChild = async (childId, relationshipData) => {
    try {
        return await axiosApiClient.post(`${PATH}/${childId}/guardians`, relationshipData);
    } catch (err) {
        throw new Error(err.response?.data?.message || 'Add Guardian to Child failed');
    }
};

export const updateGuardianRelationship = async (childId, guardianId, relationshipData) => {
    try {
        return await axiosApiClient.put(`${PATH}/${childId}/guardians/${guardianId}`, relationshipData);
    } catch (err) {
        throw new Error(err.response?.data?.message || 'Update Guardian Relationship failed');
    }
};

// Get child images
export const getChildImages = async (childId) => {
    try {
        const response = await axiosApiClient.get(`${PATH}/${childId}/images`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to fetch child images");
    }
};

// Upload child images
export const uploadChildImages = async (childId, formData) => {
    try {
        const response = await axiosApiClient.post(`${PATH}/${childId}/images`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to upload child images");
    }
};

// Delete child image
export const deleteChildImage = async (childId, imageId) => {
    try {
        const response = await axiosApiClient.delete(`${PATH}/${childId}/images/${imageId}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to delete child image");
    }
};

/**
 * Get children for the authenticated user based on their role.
 * - Called with no args: returns full list (same as before; used by dashboard, KidFaceDetection, WatchMyKidHls, mobile).
 * - Called with (page, size): returns one page and response includes totalElements, hasNext, etc. (used by KidMJPEGStreaming).
 * - Called with ({paginated, page, size, branchId, roomId}): returns paginated results with optional filters.
 *
 * @param {Object|number} [page] - Either an object with params {paginated, page, size, branchId, roomId} OR a number for page index
 * @param {number} [size] - Page size (used when page is a number); default 20
 * @returns {Promise<Object>} - { children, count, ... } and when paginated: totalElements, hasNext, totalPages, etc.
 */
export const getMyChildren = async (page, size = 20) => {
    try {
        let params = {};
        
        // Handle object param mode (with branchId/roomId filters)
        if (typeof page === 'object' && page !== null) {
            params = { ...page };
        } 
        // Handle separate page/size args mode (backward compatibility)
        else if (typeof page === 'number') {
            params = { paginated: true, page, size };
        }
        
        const response = await axiosApiClient.get(`${PATH}/my-children`, { params });
        return response.data;
    } catch (err) {
        throw new Error(err.response?.data?.message || 'Failed to get children');
    }
};

/**
 * Create SSE connection for kid face detection streaming
 * First gets a token, then uses it for secure streaming
 * @param {number} childId - The child ID
 * @param {Function} onFrame - Callback for received frames
 * @param {Function} onConnect - Callback for connection established
 * @param {Function} onError - Callback for connection errors
 * @returns {Object} - The SSE connection object with close method
 */
export const createKidStream = async (childId, onFrame, onConnect, onError) => {
    try {
        const baseUrl = getBackendUrl();
        const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

        console.log('Getting token for child ID:', childId);

        // First, get a token for this child
        const tokenResponse = await fetch(`${cleanBaseUrl}/public/kid-streaming/${childId}/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!tokenResponse.ok) {
            throw new Error(`Failed to get token: ${tokenResponse.status} ${tokenResponse.statusText}`);
        }

        const tokenData = await tokenResponse.json();
        if (!tokenData.success) {
            throw new Error(`Token generation failed: ${tokenData.message}`);
        }

        const token = tokenData.token;
        console.log('Got token for child ID:', childId);

        // Now create the streaming connection with the token
        const streamUrl = `${cleanBaseUrl}/public/kid-streaming/${childId}/stream?token=${encodeURIComponent(token)}`;
        console.log('Creating SECURE kid SSE connection for child ID:', childId, 'URL:', streamUrl);

        // Create EventSource for secure endpoint with token
        const eventSource = new EventSource(streamUrl);

        // Handle connection open
        eventSource.onopen = () => {
            console.log('Kid SSE connection opened for child ID:', childId);
            if (onConnect) onConnect();
        };

        // Handle frame events
        eventSource.addEventListener('frame', (event) => {
            try {
                const eventData = JSON.parse(event.data);
                console.log('Kid SSE frame event for child ID:', childId, 'Frame data length:', eventData.frameData?.length || 0);
                if (onFrame) onFrame(eventData);
            } catch (err) {
                console.error('Error parsing kid SSE frame data for child ID:', childId, err);
                console.error('Raw frame data:', event.data);
            }
        });

        // Handle connection events
        eventSource.addEventListener('connected', (event) => {
            try {
                const connectionData = JSON.parse(event.data);
                console.log('Kid SSE connected event for child ID:', childId, connectionData);
            } catch (err) {
                console.error('Error parsing kid SSE connection data for child ID:', childId, err);
            }
        });

        // Handle errors
        eventSource.onerror = (error) => {
            console.error('Kid SSE connection error for child ID:', childId, error);
            if (onError) onError(error);
        };

        // Return an object with close and stop methods
        return {
            close: () => {
                console.log('Closing kid SSE connection for child ID:', childId);
                eventSource.close();
            },
            stop: () => {
                console.log('Stopping kid stream for child ID:', childId);
                eventSource.close();
                // Send stop request to backend
                return stopKidStream(childId);
            }
        };

    } catch (err) {
        console.error('Error creating kid SSE connection for child ID:', childId, err);
        if (onError) onError(err);
        return null;
    }
};

/**
 * Stop kid streaming for a specific child
 * @param {number} childId - The child ID
 * @returns {Promise<Object>} - Response from stop endpoint
 */
export const stopKidStream = async (childId) => {
    try {
        const baseUrl = getBackendUrl();
        const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

        console.log('Stopping kid stream for child ID:', childId);

        const response = await fetch(`${cleanBaseUrl}/public/kid-streaming/${childId}/stop`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId: 'frontend-user' // You can customize this based on user context
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to stop stream: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Stop stream response:', result);
        return result;

    } catch (err) {
        console.error('Error stopping kid stream for child ID:', childId, err);
        throw err;
    }
};

// Default export for backward compatibility
const childApiClient = {
    getAllChildren,
    getChildrenById,
    getChildrenDetailById,
    getChildDetail,
    getAllChildrenByGuardianId,
    addChild,
    updateChild,
    deleteChild,
    removeGuardianFromChild,
    addGuardianToChild,
    updateGuardianRelationship,
    getChildImages,
    uploadChildImages,
    deleteChildImage,
    getMyChildren,
    createKidStream,
    stopKidStream
};

export default childApiClient;
